import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../../api/client';
import { DEVICE_TYPES, DEVICE_PLACEMENTS, formatDevicePlacementLabel, formatDeviceTypeLabel, type DeviceType } from '../devices/deviceTypes';

export type DeviceRow = {
  _id: string;
  clientId?: string;
  type: DeviceType | string;
  manufacturer?: string;
  modelName?: string;
  modelNumber?: string;
  serialNumber?: string;
  installationLocation?: string;
  floor?: string;
  zone?: string;
  placement?: string;
  installationDate?: string;
  lastInspectedAt?: string | null;
  lastInspectionResult?: 'pass' | 'fail' | 'not_tested' | string;
  status?: 'active' | 'decommissioned';
};

const emptyDraft = (): Record<string, string> => ({
  type: 'smoke_detector',
  manufacturer: '',
  modelName: '',
  modelNumber: '',
  serialNumber: '',
  installationLocation: '',
  floor: '',
  zone: '',
  placement: 'unspecified',
  installationDate: '',
  notes: ''
});

type Props = { clientId: string };

export default function ClientDevicesSection({ clientId }: Props): React.JSX.Element {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [showDecommissioned, setShowDecommissioned] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<'add' | 'edit' | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [openingEdit, setOpeningEdit] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const qs = new URLSearchParams({ clientId });
      if (showDecommissioned) qs.set('includeDecommissioned', 'true');
      const res = await apiRequest<{ success: boolean; data: DeviceRow[] }>(`/api/devices?${qs.toString()}`);
      setDevices(res.data || []);
    } catch (e: unknown) {
      setDevices([]);
      setLoadError(e instanceof Error ? e.message : 'Could not load devices.');
    }
  }, [clientId, showDecommissioned]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  function openAdd() {
    setEditId(null);
    setDraft(emptyDraft());
    setModalOpen('add');
  }

  async function openEdit(row: DeviceRow) {
    setEditId(row._id);
    setOpeningEdit(true);
    try {
      const res = await apiRequest<{ success: boolean; data: DeviceRow & { notes?: string } }>(`/api/devices/${row._id}`);
      const d = res.data;
      setDraft({
        type: d.type,
        manufacturer: d.manufacturer || '',
        modelName: d.modelName || '',
        modelNumber: d.modelNumber || '',
        serialNumber: d.serialNumber || '',
        installationLocation: d.installationLocation || '',
        floor: d.floor || '',
        zone: d.zone || '',
        placement: d.placement || 'unspecified',
        installationDate: d.installationDate ? String(d.installationDate).slice(0, 10) : '',
        notes: d.notes || ''
      });
      setModalOpen('edit');
    } catch {
      window.alert('Could not load device details.');
    } finally {
      setOpeningEdit(false);
    }
  }

  async function saveModal() {
    setSaving(true);
    try {
      const body = {
        clientId,
        type: draft.type,
        manufacturer: draft.manufacturer,
        modelName: draft.modelName,
        modelNumber: draft.modelNumber,
        serialNumber: draft.serialNumber,
        installationLocation: draft.installationLocation,
        floor: draft.floor,
        zone: draft.zone,
        placement: draft.placement || 'unspecified',
        installationDate: draft.installationDate || undefined,
        notes: draft.notes
      };

      if (modalOpen === 'add') {
        await apiRequest('/api/devices', { method: 'POST', json: body });
      } else if (modalOpen === 'edit' && editId) {
        await apiRequest(`/api/devices/${editId}`, { method: 'PATCH', json: body });
      }
      setModalOpen(null);
      await load();
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function decommission(deviceId: string) {
    if (!window.confirm('Decommission this device? It will be hidden from the default view.')) return;
    try {
      await apiRequest(`/api/devices/${deviceId}`, { method: 'DELETE' });
      await load();
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : 'Could not decommission.');
    }
  }

  function resultBadge(code?: string) {
    const base = { display: 'inline-block' as const, padding: '2px 8px', borderRadius: 999, fontWeight: 800, fontSize: '0.72rem' };
    if (code === 'pass') return <span style={{ ...base, background: 'rgba(46,138,87,0.12)', color: '#2e8a57' }}>Pass</span>;
    if (code === 'fail') return <span style={{ ...base, background: 'rgba(192,57,43,0.12)', color: '#c0392b' }}>Fail</span>;
    return <span style={{ ...base, background: '#f3f4f6', color: '#64748b' }}>—</span>;
  }

  const statusBadge = (s?: string) => {
    const base = { display: 'inline-block' as const, padding: '2px 8px', borderRadius: 999, fontWeight: 700, fontSize: '0.72rem' };
    return s === 'decommissioned' ? (
      <span style={{ ...base, background: '#f3f4f6', color: '#64748b' }}>Decommissioned</span>
    ) : (
      <span style={{ ...base, background: 'rgba(46,138,87,0.12)', color: '#2e8a57' }}>Active</span>
    );
  };

  return (
    <>
      <section className="quote-card" style={{ marginTop: '22px', padding: '20px 24px', maxWidth: '1100px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div className="quote-section-title" style={{ margin: 0 }}>
            Devices at this building
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.88rem', color: 'var(--dash-muted)' }}>
              <input type="checkbox" checked={showDecommissioned} onChange={(e) => setShowDecommissioned(e.target.checked)} />
              Show decommissioned
            </label>
            <button type="button" className="btn-primary" onClick={openAdd}>
              Add device
            </button>
          </div>
        </div>

        {loadError && <div className="banner banner-error" style={{ marginBottom: '12px' }}>{loadError}</div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.08)', textAlign: 'left', color: 'var(--dash-muted)' }}>
                <th style={{ padding: '10px 8px' }}>Type</th>
                <th style={{ padding: '10px 8px' }}>Manufacturer</th>
                <th style={{ padding: '10px 8px' }}>Model</th>
                <th style={{ padding: '10px 8px' }}>Location</th>
                <th style={{ padding: '10px 8px' }}>Area</th>
                <th style={{ padding: '10px 8px' }}>Floor</th>
                <th style={{ padding: '10px 8px' }}>Last inspected</th>
                <th style={{ padding: '10px 8px' }}>Last result</th>
                <th style={{ padding: '10px 8px' }}>Status</th>
                <th style={{ padding: '10px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '18px', color: 'var(--dash-muted)', textAlign: 'center' }}>
                    No devices yet. Add asset records your technicians inspect against during jobs.
                  </td>
                </tr>
              ) : (
                devices.map((d) => (
                  <tr key={d._id} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/devices/${d._id}`)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          color: 'var(--dash-accent-strong, #0f766e)',
                          fontWeight: 700,
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        {formatDeviceTypeLabel(d.type)}
                      </button>
                    </td>
                    <td style={{ padding: '10px 8px' }}>{d.manufacturer || '—'}</td>
                    <td style={{ padding: '10px 8px' }}>{d.modelName || d.modelNumber || '—'}</td>
                    <td style={{ padding: '10px 8px' }}>{d.installationLocation || '—'}</td>
                    <td style={{ padding: '10px 8px' }}>{formatDevicePlacementLabel(d.placement)}</td>
                    <td style={{ padding: '10px 8px' }}>{d.floor || '—'}</td>
                    <td style={{ padding: '10px 8px' }}>
                      {d.lastInspectedAt ? new Date(d.lastInspectedAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 8px' }}>{resultBadge(d.lastInspectionResult)}</td>
                    <td style={{ padding: '10px 8px' }}>{statusBadge(d.status)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ marginRight: 8 }}
                        disabled={openingEdit}
                        onClick={() => openEdit(d).catch(() => undefined)}
                      >
                        Edit
                      </button>
                      {d.status === 'active' && (
                        <button type="button" className="btn-secondary" onClick={() => decommission(d._id)}>
                          Decommission
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div className="quote-card" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
            <div className="quote-section-title" style={{ marginBottom: 14 }}>
              {modalOpen === 'add' ? 'Register device' : 'Edit device'}
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label className="form-label">Type</label>
                <select className="form-input" value={draft.type} onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}>
                  {DEVICE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {formatDeviceTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Manufacturer</label>
                <input className="form-input" value={draft.manufacturer} onChange={(e) => setDraft((p) => ({ ...p, manufacturer: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Model name</label>
                <input className="form-input" value={draft.modelName} onChange={(e) => setDraft((p) => ({ ...p, modelName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Model number</label>
                <input className="form-input" value={draft.modelNumber} onChange={(e) => setDraft((p) => ({ ...p, modelNumber: e.target.value }))} />
              </div>
              <div className="form-group full-width">
                <label className="form-label">Serial number</label>
                <input className="form-input" value={draft.serialNumber} onChange={(e) => setDraft((p) => ({ ...p, serialNumber: e.target.value }))} />
              </div>
              <div className="form-group full-width">
                <label className="form-label">Installation location</label>
                <input className="form-input" value={draft.installationLocation} onChange={(e) => setDraft((p) => ({ ...p, installationLocation: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Floor</label>
                <input className="form-input" value={draft.floor} onChange={(e) => setDraft((p) => ({ ...p, floor: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Zone</label>
                <input className="form-input" value={draft.zone} onChange={(e) => setDraft((p) => ({ ...p, zone: e.target.value }))} />
              </div>
              <div className="form-group full-width">
                <label className="form-label">Area (annual report)</label>
                <select className="form-input" value={draft.placement} onChange={(e) => setDraft((p) => ({ ...p, placement: e.target.value }))}>
                  {DEVICE_PLACEMENTS.map((p) => (
                    <option key={p} value={p}>
                      {formatDevicePlacementLabel(p)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group full-width">
                <label className="form-label">Installation date</label>
                <input className="form-input" type="date" value={draft.installationDate} onChange={(e) => setDraft((p) => ({ ...p, installationDate: e.target.value }))} />
              </div>
              <div className="form-group full-width">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={draft.notes} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn-secondary" disabled={saving} onClick={() => setModalOpen(null)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={saving} onClick={() => saveModal().catch(() => undefined)}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
